// Zod contracts for the staff-side New Client Offer surfaces: the calendar card badge, the Offer row in the
// appointment panel's client card, the staff Undo / Not eligible actions, and the offer a visit can be given at
// Hayven checkout.
import { z } from 'zod';
import {
  offerClaimStatusSchema,
  offerDiscountTypeSchema,
  offerRedemptionMethodSchema,
  offerTimestampSchema,
} from './new-client-offer-schemas';

/** Which gift a desk badge names. Absent means NEW_CLIENT: only a voucher sets it, so offer payloads stay as they were. */
export const visitOfferKindSchema = z.enum(['NEW_CLIENT', 'VOUCHER']);
export type VisitOfferKind = z.infer<typeof visitOfferKindSchema>;

/**
 * Calendar / week card badge: present while a claim is BOOKED on (or REDEEMED by) that appointment, else while the
 * visit's check-in holds a voucher or the visit used one.
 */
export const calendarOfferBadgeSchema = z.object({ label: z.string(), offerKind: visitOfferKindSchema.optional() });
export type CalendarOfferBadge = z.infer<typeof calendarOfferBadgeSchema>;

/**
 * Why staff cannot undo a redemption from the app: the guest has a checked-out visit since signing up (or the
 * redeeming visit is still checked out), so the appointment sync would redeem it again — staff reopen that
 * visit first.
 */
export const offerUndoBlockedReasonSchema = z.enum(['VISIT_COMPLETED']);
export type OfferUndoBlockedReason = z.infer<typeof offerUndoBlockedReasonSchema>;

/**
 * The claim an appointment holds (BOOKED / NO_SHOW) or consumed (REDEEMED), for the appointment panel's
 * Offer row. The discount snapshot is what the guest was promised; the panel computes the POS amount from it.
 */
export const appointmentOfferClaimSchema = z.object({
  id: z.string(),
  label: z.string(),
  headline: z.string(),
  discountType: offerDiscountTypeSchema,
  percentOff: z.number().int().nullable(),
  amountOffCents: z.number().int().nullable(),
  status: offerClaimStatusSchema,
  expiresAt: offerTimestampSchema,
  redeemedAt: offerTimestampSchema.nullable(),
  redemptionMethod: offerRedemptionMethodSchema.nullable(),
  /** Who used it: the staff member who marked the visit Done, or who redeemed it by hand (historic Offer
   *  lookup); null when unknown (Auto-Done, legacy check-in redemptions). */
  redeemedByName: z.string().nullable(),
  /** Set when the claim is REDEEMED but Undo is not offered (see `offerUndoBlockedReasonSchema`). */
  undoBlockedReason: offerUndoBlockedReasonSchema.nullable(),
  /** REDEEMED by this visit (its take-off belongs on this bill), not by another visit of the guest. Optional:
   *  an older API omits it. */
  redeemedByThisVisit: z.boolean().optional(),
});
export type AppointmentOfferClaim = z.infer<typeof appointmentOfferClaimSchema>;

/** The claim after a staff action (Undo / Not eligible). */
export const offerClaimStaffViewSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  status: offerClaimStatusSchema,
  label: z.string(),
  expiresAt: offerTimestampSchema,
  /** The visit currently holding the claim (BOOKED / NO_SHOW). */
  appointmentId: z.string().nullable(),
  redeemedAt: offerTimestampSchema.nullable(),
  undoBlockedReason: offerUndoBlockedReasonSchema.nullable(),
});
export type OfferClaimStaffView = z.infer<typeof offerClaimStaffViewSchema>;

/** Staff action outcome. `changed: false` = it had already happened (e.g. already ruled out). */
export const offerClaimActionResultSchema = z.object({
  changed: z.boolean(),
  claim: offerClaimStaffViewSchema,
});
export type OfferClaimActionResult = z.infer<typeof offerClaimActionResultSchema>;

/** 409 reasons of the staff claim actions. */
export const offerClaimActionRejectionSchema = z.enum([
  'CLAIM_CHANGED',
  'NOT_REDEEMED',
  'VISIT_COMPLETED',
  'CANNOT_MARK_NOT_ELIGIBLE',
]);
export type OfferClaimActionRejection = z.infer<typeof offerClaimActionRejectionSchema>;

/** Most visits one Hayven checkout asks about (a group checkout's members). */
export const CHECKOUT_OFFER_VISIT_LIMIT = 20;

/**
 * A visit's New Client Offer at Hayven checkout: the claim it holds while BOOKED (booked, or held when the
 * guest checked in), else the one it REDEEMED — the same claim the calendar badge shows. A checkout line of that visit
 * gives it by carrying `claimId`; the server computes the discount from the snapshot below.
 */
export const checkoutOfferSchema = z.object({
  claimId: z.string(),
  appointmentId: z.string(),
  label: z.string(),
  status: z.enum(['BOOKED', 'REDEEMED']),
  discountType: offerDiscountTypeSchema,
  percentOff: z.number().int().nullable(),
  amountOffCents: z.number().int().nullable(),
});
export type CheckoutOffer = z.infer<typeof checkoutOfferSchema>;
